import React, { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { setdeliveryType } from "store/slices/main.slice";

// === ADDED BY SAMPATH – 2026-01-16 ===
// (No new imports needed; using existing tailwind/theme classes)
// === END SAMPATH ===

interface OrderTypePopupProps {
  setStage: (v: string) => void;
}

const OrderType: React.FC<OrderTypePopupProps> = ({ setStage }) => {
  const [orderType, setOrderType] = useState<"pickup" | "delivery">("pickup");
  const dispatch = useAppDispatch();
  const settings = useAppSelector(({ main }) => main.settings);

  // === ADDED BY SAMPATH – 2026-01-16 ===
  // Pull companyData as well (same as DeliveryTypePopUp design)
  const companyData = useAppSelector(({ main }) => main.companyData);

  // Target this popup root so we can find the *current* dialog paper and neutralize it
  const rootRef = useRef<HTMLDivElement | null>(null);
  // === END SAMPATH ===

  const handleSubmit = () => {
    if (orderType !== "pickup") {
      dispatch(setdeliveryType(orderType));
    } else {
      dispatch(setdeliveryType(orderType));
    }
    setStage("CART");
  };

  useEffect(() => {
    if (settings.ClosePickupOrder !== 1) {
      setOrderType("pickup");
      if (settings.CloseDeliveryOrder === 1) {
        dispatch(setdeliveryType("pickup"));
        setStage("CART");
      }
    } else if (settings.CloseDeliveryOrder !== 1) {
      setOrderType("delivery");
      if (settings.ClosePickupOrder === 1) {
        dispatch(setdeliveryType("delivery"));
        setStage("CART");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // === ADDED BY SAMPATH – 2026-01-16 ===
  // Ensure full-screen overlay even when rendered inside MUI Dialog/Paper (fix for not covering whole viewport)
  // Also lock body scroll while this popup is open
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-16 ===
  // REMOVE the unwanted MUI Dialog "Paper" panel behind the overlay (MuiPaper-root...MuiDialog-paper...)
  // We only neutralize the *parent dialog paper* that contains THIS component, and restore on unmount.
  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;

    // Find the closest dialog paper that is wrapping this content
    const paper = rootEl.closest(".MuiDialog-paper") as HTMLElement | null;
    if (!paper) return;

    // Backup existing inline styles so we can restore safely
    const prev = {
      background: paper.style.background,
      boxShadow: paper.style.boxShadow,
      padding: paper.style.padding,
      margin: paper.style.margin,
      minWidth: paper.style.minWidth,
      minHeight: paper.style.minHeight,
      width: paper.style.width,
      height: paper.style.height,
      maxWidth: paper.style.maxWidth,
      maxHeight: paper.style.maxHeight,
      overflow: paper.style.overflow,
      border: paper.style.border,
    };

    // Neutralize the panel
    paper.style.background = "transparent";
    paper.style.boxShadow = "none";
    paper.style.border = "none";
    paper.style.padding = "0";
    paper.style.margin = "0";
    paper.style.minWidth = "0";
    paper.style.minHeight = "0";
    paper.style.width = "auto";
    paper.style.height = "auto";
    paper.style.maxWidth = "none";
    paper.style.maxHeight = "none";
    paper.style.overflow = "visible";

    return () => {
      paper.style.background = prev.background;
      paper.style.boxShadow = prev.boxShadow;
      paper.style.padding = prev.padding;
      paper.style.margin = prev.margin;
      paper.style.minWidth = prev.minWidth;
      paper.style.minHeight = prev.minHeight;
      paper.style.width = prev.width;
      paper.style.height = prev.height;
      paper.style.maxWidth = prev.maxWidth;
      paper.style.maxHeight = prev.maxHeight;
      paper.style.overflow = prev.overflow;
      paper.style.border = prev.border;
    };
  }, []);
  // === END SAMPATH ===

  return (
    // === ADDED BY SAMPATH – 2026-01-16 ===
    // Use fixed inset-0 so backdrop covers the full viewport (not limited by dialog container)
    <div
      ref={rootRef}
      className="fixed inset-0 w-screen h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-black/1 backdrop-blur-md"
    >
      {/* Glass container (like DeliveryTypePopUp) */}
      <div className="bg-white/90 backdrop-blur-xl border border-white/40 p-6 rounded-2xl shadow-2xl max-w-sm w-full">
        {/* Header area with logo/company name (same vibe as popup) */}
        <div className="flex flex-col items-center">
          {settings?.showLogo !== 0 ? (
            <div
              onDoubleClick={() => {
                window.location.href = "/admin/setting/time";
              }}
              className="text-white !px-1.5 relative"
            >
              <img
                src={companyData?.logoUrl}
                alt=""
                className="mb-4 max-h-20 max-w-[200px] object-contain"
                loading="eager"
                draggable={false}
              />
            </div>
          ) : null}

          {!!settings?.ShowCompanyName && !!companyData?.companyName && (
            <h1 className="text-xl font-bold text-primary">
              {companyData?.companyName}
            </h1>
          )}
        </div>
        {/* === END SAMPATH === */}

        {(settings.CloseDeliveryOrder !== 1 ||
          settings.ClosePickupOrder !== 1) && (
          <div className="flex flex-col items-center text-center mt-2">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              Choose Your Order Type
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              Select whether you'd like to pick up your order or have it
              delivered.
            </p>
          </div>
        )}

        {/* === ADDED BY SAMPATH – 2026-01-16 === */}
        {/* Glass pill toggle background (match DeliveryTypePopUp) */}
        <div className="flex gap-2 bg-white/95 border border-white/40 rounded-full p-1 mt-5 shadow-inner">
          {settings.ClosePickupOrder !== 1 && (
            <button
              className={`${
                orderType === "pickup"
                  ? "bg-primary text-themeSelColor shadow"
                  : "bg-transparent text-gray-800"
              } py-2 px-4 transition-colors duration-300 rounded-full flex-1`}
              onClick={() => setOrderType("pickup")}
              type="button"
            >
              Pickup
            </button>
          )}

          {settings.CloseDeliveryOrder !== 1 && (
            <button
              className={`${
                orderType === "delivery"
                  ? "bg-primary text-themeSelColor shadow"
                  : "bg-transparent text-gray-800"
              } py-2 px-4 transition-colors duration-300 rounded-full flex-1`}
              onClick={() => setOrderType("delivery")}
              type="button"
            >
              Delivery
            </button>
          )}
        </div>
        {/* === END SAMPATH === */}

        {/* Continue Button */}
        {(settings.CloseDeliveryOrder !== 1 ||
          settings.ClosePickupOrder !== 1) && (
          <Button
            onClick={handleSubmit}
            disabled={!orderType}
            variant="contained"
            // === ADDED BY SAMPATH – 2026-01-16 ===
            // Match popup button style (rounded-xl + shadow)
            className="!mt-6 !w-full !bg-primary !text-themeSelColor !rounded-xl !py-3 !shadow-lg"
            // === END SAMPATH ===
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
};

export default OrderType;
